<template>
  <div class="spreadsheet-upload">
    <v-card
      class="spreadsheet-upload__source-section pa-5 mt-5"
      :class="{ active: sourceFile }"
    >
      <h2>Client Files</h2>
      <SourceInput />
      <v-scale-transition
        ><SourceDropzone
          v-if="
            sourceFile &&
            !xContent &&
            !shakespeare &&
            !shakespeareLoc &&
            !cueCopywriting
          "
      /></v-scale-transition>
    </v-card>
    <v-scale-transition>
      <div>
        <v-card class="pa-5 mt-5" v-if="sourceFile">
          <SharedData 
            :assignmentFieldSections="assignmentFieldSections"
            @files-updated="filesUpdated"/>
        </v-card>
        <v-card class="pa-5 mt-5" v-if="sourceFile">
          <SharedDeadlines 
            ref="sharedDeadlinesComponent"
            :assignmentsData="individualAssignmentsData"/>
        </v-card>
        <v-card class="pa-5 mt-5" v-if="sourceFile">
          <AssignmentsSection 
            ref="assignmentsSectionComponent"
            @updateAssignmentsList="updateAssignmentsList"
            @open-assignments-fields-modal="assignmentsFieldsModal = true"
            :assignmentFieldSections="assignmentFieldSections"
            :deliveryDateRefs="$refs.sharedDeadlinesComponent?.$refs?.deliveryDateTimeMenu?.isActive"/>
        </v-card>
        <v-card class="pa-5 mt-5" v-if="sourceFile">
          <div class="capacity-section">
            <div class="capacity-section__header" @click="toggleCapacity">
              <h2>Capacity</h2>
              <v-icon class="capacity-section__chevron" :class="{ 'capacity-section__chevron--open': capacityOpen }">
                mdi-chevron-down
              </v-icon>
            </div>
            <v-expand-transition>
              <div v-if="capacityOpen" class="capacity-section__body">
                <div
                  v-for="(item, index) in capacityItems"
                  :key="`${item.staffId}-${item.role}`"
                  class="capacity-item"
                  :class="{ 'capacity-item--divided': index > 0 }"
                >
                  <div class="capacity-item__name">
                    {{ item.name }}
                    <span class="capacity-item__role" :style="{ color: item.color }">
                      {{ item.roleLabel }}
                    </span>
                  </div>
                  <div class="capacity-item__row">
                    <div class="capacity-item__col capacity-item__col--current">
                      <div class="capacity-item__label">CURRENT</div>
                      <div class="capacity-item__current">
                        <span class="capacity-item__current-prefix">W:</span>
                        <input
                          class="capacity-item__input"
                          type="text"
                          :value="item.usedWords + item.batchWords"
                          readonly
                        />
                      </div>
                    </div>
                    <div class="capacity-item__col capacity-item__col--bar">
                      <div class="capacity-item__label capacity-item__label--center">
                        TOTAL/MAX
                      </div>
                      <div
                        class="capacity-item__bar"
                        :style="{ backgroundColor: item.lightColor }"
                      >
                        <div
                          class="capacity-item__bar-used"
                          :style="{
                            width: capacityUsedPct(item) + '%',
                            backgroundColor: item.color,
                          }"
                        ></div>
                        <div
                          v-if="item.batchWords"
                          class="capacity-item__bar-batch"
                          :style="capacityBatchStyle(item)"
                        ></div>
                        <span class="capacity-item__bar-text">
                          {{ item.usedWords }}/{{ item.totalAllowedWords }}
                        </span>
                      </div>
                    </div>
                    <div class="capacity-item__col capacity-item__col--left">
                      <div class="capacity-item__label capacity-item__label--right">
                        LEFT
                      </div>
                      <div
                        class="capacity-item__value"
                        :class="{ 'capacity-item__value--over': item.left < 0 }"
                      >
                        {{ item.left }}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="!capacityItems.length" class="capacity-section__empty">
                  No assignees selected yet. Assign primary, secondary, or QC staff to see their capacity.
                </div>
              </div>
            </v-expand-transition>
          </div>
        </v-card>
        <div class="submit-container pa-5 mt-5" v-if="sourceFile">
          <button class="submit-button" 
            :disabled="isSubmitting"
            @click="submit">
            <v-progress-circular
              v-if="isSubmitting"
              indeterminate
              size="18"
              width="2"
              color="white"
              class="submit-button__spinner"
            />
            <span v-if="isSubmitting">Creating assignments...</span>
            <span v-else>Create {{ individualAssignmentsData.length }} assignments</span>
          </button>
        </div>
        <v-alert
        v-model="successAlert"
        class="success-alert"
        type="success"
        dismissible
      >
        Import Successful
        <v-btn
          class="ml-2"
          small
          depressed
          light
          :href="exportedPath"
          target="_blank"
          >Check on Dashboard</v-btn
        >
      </v-alert>
      <v-alert
        v-model="failAlert"
        class="fail-alert"
        type="error"
        dismissible
        >{{ failAlertMessage }}</v-alert
      >
      </div>
    </v-scale-transition>
    <AssignmentsFieldsModal
      v-model="assignmentsFieldsModal"
      :shared-fields="sharedModalFields"
      :individual-fields="individualModalFields"
      :individual-eligible-fields="individualEligibleModalFields"
      @save="onAssignmentsFieldsSave"
    />
  </div>
</template>

<script>
import { DOMAIN, ROUTES } from "@/utils/constants";
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import { 
  getAllProjects,
  getAllStaff,
  getSpecificEnum,
  getCustomerPriceList,
  processAssignmentsList,
  createDocumentMetadataBatch,
  attachFilesToAssignments
} from "@/utils/newDbUtils";
import SourceDropzone from "@/components/SpreadsheetUpload/SourceDropzone";
import SourceInput from "@/components/SpreadsheetUpload/SourceInput";
import SharedData from "@/components/SpreadsheetUpload/SharedData";
import SharedDeadlines from "@/components/SpreadsheetUpload/SharedDeadlines";
import AssignmentsSection from "@/components/SpreadsheetUpload/AssignmentsSection.vue";
import AssignmentsFieldsModal from "@/components/SpreadsheetUpload/AssignmentsFieldsModal.vue";

const ASSIGNMENT_FIELD_LABELS = {
  assignmentTitle: "Assignment Title",
  assignmentId: "Assignment ID",
  requestType: "Request Type",
  assignmentDetail: "Assignment Detail",
  geo: "GEO",
};

const DEFAULT_ASSIGNMENT_FIELD_SECTIONS = {
  shared: [
    "assignmentTitle",
  ],
  individual: [
    "assignmentId",
    "assignmentDetail",
    "requestType",
    "geo",
  ],
};

const ASSIGNMENT_FIELD_KEY_BY_LABEL = Object.entries(ASSIGNMENT_FIELD_LABELS).reduce(
  (acc, [fieldKey, label]) => {
    acc[label] = fieldKey;
    return acc;
  },
  {}
);

const INDIVIDUAL_ELIGIBLE_FIELDS = new Set([
  "assignmentTitle",
  "assignmentId",
  "assignmentDetail",
  "requestType",
  "geo",
]);

// Per-role bar colors. `color` fills used capacity and forms the dotted overlay
// for words being added; `lightColor` is the brighter empty-capacity track.
const CAPACITY_ROLE_PALETTE = {
  PRIMARY: { color: "#9EB9F3", lightColor: "#D6E0FA" },
  SECONDARY: { color: "#DCB0F2", lightColor: "#EFDBF9" },
  QC: { color: "#B8B8B8", lightColor: "#DCDCDC" },
};

// Assignment fields holding each role's selected staff.
const ROLE_BY_ASSIGNEE_KEY = {
  primaryAssignee: "PRIMARY",
  secondaryAssignee: "SECONDARY",
  qcAssignee: "QC",
};
const ASSIGNEE_KEYS = Object.keys(ROLE_BY_ASSIGNEE_KEY);
const ROLE_LABELS = { PRIMARY: "Primary", SECONDARY: "Secondary", QC: "QC" };

export default {
  components: {
    SourceDropzone,
    SourceInput,
    SharedData,
    SharedDeadlines,
    AssignmentsSection,
    AssignmentsFieldsModal
  },
  data() {
    return {
      individualAssignmentsData: [],
      allWorkTypes: [],
      staffList: [],
      filesList: null,
      successAlert: false,
      failAlert: false,
      failAlertMessage: "Something went wrong",
      isSubmitting: false,
      capacityOpen: false,
      capacityItems: [],
      assignmentsFieldsModal: false,
      assignmentFieldSections: {
        shared: [...DEFAULT_ASSIGNMENT_FIELD_SECTIONS.shared],
        individual: [...DEFAULT_ASSIGNMENT_FIELD_SECTIONS.individual],
      },
    };
  },
  watch: {
    files(newValue) {
      // check for and set if files to process match source file
      const filenames = (newValue || []).map((el) => el.name.split(".xlsx")[0]);
      const sourceNames = (this.tableRows || []).map((el) => el.task_name);

      this.setAllSourceFilesFound(
        _.difference(filenames, sourceNames).length === 0 &&
          filenames.length > 0 &&
          sourceNames.length > 0
      );
    },
    tableRows(newValue) {
      const filenames = (this.files || []).map((el) => el.name.split(".xlsx")[0]);
      const sourceNames = (newValue || []).map((el) => el.task_name);
      this.setAllSourceFilesFound(
        _.difference(filenames, sourceNames).length === 0 &&
          filenames.length > 0 &&
          sourceNames.length > 0
      );
      this.prefillSharedFields(newValue);
    },
    "formValues.relatedProject"(newValue) {
      if (newValue && typeof newValue === "object") {
        this.prefillClientRequesterFromSource(this.tableRows);
      }
    },
    mediaTypeOptions() {
      this.prefillMediaKeywordGenreFromSource(this.tableRows);
    },
    keywordsOptions() {
      this.prefillMediaKeywordGenreFromSource(this.tableRows);
    },
  },
  methods: {
    ...mapActions("assignment", ["setCommonData"]),
    ...mapActions("spreadsheetUpload", ["setAllSourceFilesFound", "setstaffListData", "setDubEditor", "setFormValue"]),
    normalizeAssignmentFieldSections(fieldLists = {}) {
      const knownFieldKeys = Object.keys(ASSIGNMENT_FIELD_LABELS);

      const sharedKeys = (fieldLists.sharedFields || [])
        .map((label) => ASSIGNMENT_FIELD_KEY_BY_LABEL[label])
        .filter((fieldKey) => !!fieldKey);
      const individualKeys = (fieldLists.individualFields || [])
        .map((label) => ASSIGNMENT_FIELD_KEY_BY_LABEL[label])
        .filter(
          (fieldKey) =>
            !!fieldKey &&
            INDIVIDUAL_ELIGIBLE_FIELDS.has(fieldKey) &&
            !sharedKeys.includes(fieldKey)
        );

      const seenKeys = new Set([...sharedKeys, ...individualKeys]);
      knownFieldKeys.forEach((fieldKey) => {
        if (!seenKeys.has(fieldKey)) {
          sharedKeys.push(fieldKey);
        }
      });

      return {
        shared: sharedKeys,
        individual: individualKeys,
      };
    },
    hydrateSharedValuesFromAssignments(fieldSections) {
      const assignments = this.$refs.assignmentsSectionComponent?.allAssignments || [];
      if (!Array.isArray(assignments) || !assignments.length) return;
      const sampleAssignment = assignments[0];
      if (!sampleAssignment || typeof sampleAssignment !== "object") return;

      const sharedFieldToFormKeyMap = {
        assignmentTitle: "assignmentTitle",
        assignmentId: "assignmentId",
        assignmentDetail: "assignmentDetail",
        requestType: "requestType",
        geo: "geo",
      };

      Object.entries(sharedFieldToFormKeyMap).forEach(([fieldKey, formKey]) => {
        if (!fieldSections?.shared?.includes(fieldKey)) return;
        const currentValue = this.formValues?.[formKey];
        if (currentValue != null && String(currentValue).trim() !== "") return;

        const assignmentValue = sampleAssignment[fieldKey];
        if (assignmentValue == null || String(assignmentValue).trim() === "") return;
        this.setFormValue({ key: formKey, value: assignmentValue });
      });
    },
    onAssignmentsFieldsSave(fieldLists) {
      const normalizedSections = this.normalizeAssignmentFieldSections(fieldLists);
      this.assignmentFieldSections = normalizedSections;
      this.hydrateSharedValuesFromAssignments(normalizedSections);
    },
    isFieldIndividual(fieldKey) {
      return !!this.assignmentFieldSections?.individual?.includes(fieldKey);
    },
    showFailAlert(message) {
      this.failAlertMessage = message || "Something went wrong!";
      this.failAlert = true;
      setTimeout(() => (this.failAlert = false), 5000);
    },
    async flushPendingInputs() {
      if (
        typeof document !== "undefined" &&
        document.activeElement &&
        typeof document.activeElement.blur === "function"
      ) {
        document.activeElement.blur();
      }
      await this.$nextTick();
      await this.$nextTick();
    },
    normalizeAssignmentTitle(value) {
      if (value == null) return "";
      if (typeof value === "string") return value.trim();
      if (typeof value === "object") {
        return String(
          value.value ?? value.name ?? value.label ?? value.text ?? value.title ?? ""
        ).trim();
      }
      return String(value).trim();
    },
    resolveAssigneeId(assignee, fallbackName = null) {
      if (typeof assignee === "string") {
        const normalizedName = assignee.trim();
        return normalizedName ? this.getStaffItemId(normalizedName) : null;
      }
      if (assignee && typeof assignee === "object") {
        if (assignee.staffId != null) return assignee.staffId;
        if (assignee.id != null) return assignee.id;
        if (assignee.user?.id != null) return assignee.user.id;
        if (assignee.staff?.id != null) return assignee.staff.id;
        if (assignee.name) return this.getStaffItemId(assignee.name);
      }
      if (fallbackName && typeof fallbackName === "string") {
        const normalizedFallback = fallbackName.trim();
        return normalizedFallback ? this.getStaffItemId(normalizedFallback) : null;
      }
      return null;
    },
    getFileParts(file) {
      const fullName = file?.name || "";
      const lastDotIndex = fullName.lastIndexOf(".");
      const hasExtension = lastDotIndex > 0 && lastDotIndex < fullName.length - 1;

      return {
        baseName: hasExtension ? fullName.slice(0, lastDotIndex) : fullName,
        extension: hasExtension ? fullName.slice(lastDotIndex + 1) : "",
        fullName,
      };
    },
    createUploadItems() {
      const fileAccessTypes = {
        adminFiles: "ADMIN_FILE",
        workFiles: "WORK_FILE",
        staffFiles: "SHARED_FILE",
      };

      return Object.entries(fileAccessTypes).flatMap(([sourceType, fileAccessType]) => {
        const files = this.filesList?.[sourceType] || [];
        if (!Array.isArray(files)) return [];

        return files.map((file) => {
          const { baseName, extension, fullName } = this.getFileParts(file);

          return {
            file,
            sourceType,
            fileAccessType,
            fileName: fullName,
            attachmentFileName: baseName,
            fileExtension: extension,
            contentType: file.type || "application/octet-stream",
          };
        });
      });
    },
    findUploadForItem(item, uploads, matchedIndexes) {
      const candidateNames = [
        item.fileName,
        item.attachmentFileName,
        item.fileExtension ? `${item.attachmentFileName}.${item.fileExtension}` : item.attachmentFileName,
      ].filter(Boolean);

      const uploadIndex = uploads.findIndex((upload, index) => {
        if (matchedIndexes.has(index)) return false;
        return candidateNames.includes(upload?.fileName) || candidateNames.includes(upload?.url);
      });

      if (uploadIndex === -1) return null;
      matchedIndexes.add(uploadIndex);
      return uploads[uploadIndex];
    },
    async uploadAssignmentFiles() {
      const uploadItems = this.createUploadItems();
      if (!uploadItems.length) return [];

      const metadataItems = uploadItems.map(({ contentType, fileExtension, fileName }) => ({
        contentType,
        fileExtension,
        fileName,
      }));
      const metadataResponse = await createDocumentMetadataBatch({ items: metadataItems });

      if (!metadataResponse || metadataResponse.status > 204) {
        const failedMsg = metadataResponse?.data?.statusCode
          ? `${metadataResponse.data.statusCode}: ${metadataResponse.data.message}`
          : "Failed to prepare file uploads";
        throw new Error(failedMsg);
      }

      const uploads = metadataResponse?.data?.uploads || [];
      const matchedIndexes = new Set();
      const files = [];

      for (const item of uploadItems) {
        const uploadData = this.findUploadForItem(item, uploads, matchedIndexes);
        if (!uploadData?.signedUrl) {
          throw new Error(`Failed to prepare upload URL for ${item.fileName}`);
        }

        const uploadResponse = await fetch(uploadData.signedUrl, {
          method: "PUT",
          headers: { "Content-Type": item.contentType },
          body: item.file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${item.fileName}: ${uploadResponse.statusText}`);
        }

        const parsedUrl = new URL(uploadData.signedUrl);
        const fileUrl = parsedUrl.pathname.replace(/^\/[^/]+\//, "");

        files.push({
          fileName: item.attachmentFileName,
          fileUrl,
          fileExtension: item.fileExtension,
          contentType: item.contentType,
          fileAccessType: item.fileAccessType,
        });
      }

      return files;
    },
    resolveDepartmentId(departmentValue) {
      if (departmentValue == null) return null;
      if (typeof departmentValue === "number") return departmentValue;
      if (typeof departmentValue === "object" && departmentValue.id != null) {
        return departmentValue.id;
      }
      const asString = String(departmentValue).trim();
      if (!asString) return null;
      const match = (this.departmentOptions || []).find((el) => {
        const value = String(el?.value ?? "").trim();
        return value.toLowerCase() === asString.toLowerCase();
      });
      return match?.id ?? null;
    },
    normalizeText(value) {
      return String(value ?? "").trim().toLowerCase();
    },
    getOptionDisplayValue(option) {
      if (option == null) return "";
      if (typeof option === "string" || typeof option === "number") {
        return String(option).trim();
      }
      if (typeof option === "object") {
        return String(
          option.value ?? option.name ?? option.label ?? option.text ?? option.title ?? ""
        ).trim();
      }
      return "";
    },
    resolveDropdownValueFromSource(rawValue, options = []) {
      if (rawValue == null) return null;
      const raw = String(rawValue).trim();
      if (!raw) return null;
      if (!Array.isArray(options) || !options.length) return raw;

      const normalizedRaw = this.normalizeText(raw);
      const exact = options.find(
        (option) => this.normalizeText(this.getOptionDisplayValue(option)) === normalizedRaw
      );
      if (exact) return this.getOptionDisplayValue(exact);

      const partial = options.find((option) => {
        const normalizedOption = this.normalizeText(this.getOptionDisplayValue(option));
        return (
          normalizedOption &&
          (normalizedOption.includes(normalizedRaw) || normalizedRaw.includes(normalizedOption))
        );
      });
      if (partial) return this.getOptionDisplayValue(partial);

      return raw;
    },
    normalizePayPeriod(value) {
      if (!value) return null;
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return null;

      if (/^\d{4}-\d{2}$/.test(normalizedValue)) return normalizedValue;
      if (/^\d{2}-\d{4}$/.test(normalizedValue)) {
        const [month, year] = normalizedValue.split("-");
        return `${year}-${month}`;
      }

      const parsed = this.$moment(value);
      return parsed.isValid() ? parsed.format("YYYY-MM") : null;
    },
    getRequesterDisplayName(requester) {
      const firstName = requester?.user?.firstName || "";
      const lastName = requester?.user?.lastName || "";
      return `${firstName} ${lastName}`.trim();
    },
    prefillPayPeriodFromSource(rows = this.tableRows) {
      if (this.formValues?.payPeriod) return;
      if (!Array.isArray(rows) || !rows.length) return;
      if (this.shakespeare || this.shakespeareLoc) return;

      const firstRow = rows[0] || {};
      const sourcePayPeriod =
        firstRow.payPeriod ||
        firstRow.pay_period ||
        firstRow.due_date ||
        firstRow.deliveryDate ||
        firstRow.delivery_date ||
        null;
      const normalized = this.normalizePayPeriod(sourcePayPeriod);
      if (normalized) {
        this.setFormValue({ key: "payPeriod", value: normalized });
      }
    },
    prefillClientRequesterFromSource(rows = this.tableRows) {
      if (this.formValues?.clientRequester) return;

      if (this.shakespeare || this.shakespeareLoc) {
        this.setFormValue({ key: "clientRequester", value: "Shakespeare" });
        return;
      }

      const project = this.formValues?.relatedProject;
      if (!project || typeof project !== "object") return;

      const projectRequesters = project.clientRequesters || [];
      if (!projectRequesters.length) return;

      const sourceRequester = this.normalizeText(
        Array.isArray(rows) && rows.length
          ? rows[0]?.client_requester ?? rows[0]?.clientRequester
          : ""
      );

      let matchedRequester = null;
      if (sourceRequester) {
        matchedRequester = projectRequesters.find((req) => {
          const firstName = this.normalizeText(req?.user?.firstName);
          const lastName = this.normalizeText(req?.user?.lastName);
          return sourceRequester.includes(firstName) && sourceRequester.includes(lastName);
        });
      }

      if (!matchedRequester && projectRequesters.length === 1) {
        matchedRequester = projectRequesters[0];
      }

      if (matchedRequester) {
        this.setFormValue({
          key: "clientRequester",
          value: this.getRequesterDisplayName(matchedRequester),
        });
      }
    },
    prefillMediaKeywordGenreFromSource(rows = this.tableRows) {
      if (!Array.isArray(rows) || !rows.length) return;
      const firstRow = rows[0] || {};

      const sourceMediaType =
        firstRow.media_type ??
        firstRow.mediaType ??
        null;
      const sourceKeyword =
        firstRow.keyword ??
        firstRow.new_keyword ??
        firstRow.newKeyword ??
        null;
      const sourceGenre =
        firstRow.genre ??
        firstRow.genre_type ??
        firstRow.genreType ??
        null;

      if (!this.formValues?.mediaType && sourceMediaType) {
        this.setFormValue({
          key: "mediaType",
          value: this.resolveDropdownValueFromSource(sourceMediaType, this.mediaTypeOptions),
        });
      } else if (this.formValues?.mediaType && this.mediaTypeOptions?.length) {
        const normalizedCurrentMediaType = this.resolveDropdownValueFromSource(
          this.formValues.mediaType,
          this.mediaTypeOptions
        );
        if (normalizedCurrentMediaType && normalizedCurrentMediaType !== this.formValues.mediaType) {
          this.setFormValue({
            key: "mediaType",
            value: normalizedCurrentMediaType,
          });
        }
      }

      if (!this.formValues?.keyword && sourceKeyword) {
        this.setFormValue({
          key: "keyword",
          value: this.resolveDropdownValueFromSource(sourceKeyword, this.keywordsOptions),
        });
      } else if (this.formValues?.keyword && this.keywordsOptions?.length) {
        const normalizedCurrentKeyword = this.resolveDropdownValueFromSource(
          this.formValues.keyword,
          this.keywordsOptions
        );
        if (normalizedCurrentKeyword && normalizedCurrentKeyword !== this.formValues.keyword) {
          this.setFormValue({
            key: "keyword",
            value: normalizedCurrentKeyword,
          });
        }
      }

      if (!this.formValues?.genre && sourceGenre) {
        this.setFormValue({
          key: "genre",
          value: String(sourceGenre).trim(),
        });
      }
    },
    prefillSharedFields(rows = this.tableRows) {
      this.prefillPayPeriodFromSource(rows);
      this.prefillClientRequesterFromSource(rows);
      this.prefillMediaKeywordGenreFromSource(rows);
    },
    hasValue(value) {
      if (value == null) return false;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    },
    pickFirstValue(...values) {
      for (const value of values) {
        if (this.hasValue(value)) return value;
      }
      return null;
    },
    resolveSourceAssignmentId(sourceRow) {
      return this.pickFirstValue(
        sourceRow?.content_id,
        sourceRow?.video_content_id,
        sourceRow?.adam_id,
        sourceRow?.adamId,
        sourceRow?.ADAM_ID,
        sourceRow?.CONTENT_ID
      );
    },
    resolveAssignmentId(assignmentItem, sourceRow) {
      return this.pickFirstValue(
        assignmentItem?.assignmentId,
        assignmentItem?.assignment_id,
        assignmentItem?.adam_id,
        assignmentItem?.adamId,
        assignmentItem?.contentId,
        assignmentItem?.content_id,
        assignmentItem?.video_content_id,
        this.resolveSourceAssignmentId(sourceRow)
      );
    },
    resolveContentId(assignmentItem, sourceRow) {
      return this.pickFirstValue(
        assignmentItem?.contentId,
        assignmentItem?.content_id,
        assignmentItem?.video_content_id,
        sourceRow?.content_id,
        sourceRow?.video_content_id,
        sourceRow?.ADAM_ID,
        sourceRow?.CONTENT_ID
      );
    },
    getSourceRowForAssignment(assignmentItem) {
      const rows = this.tableRows || [];
      if (!rows.length) return null;

      const equals = (a, b) => String(a) === String(b);
      const assignmentTaskId = assignmentItem?.taskId ?? assignmentItem?.task_id ?? assignmentItem?.id;

      if (assignmentTaskId != null && assignmentTaskId !== "") {
        const byTaskId =
          rows.find((row) => row?.task_id != null && equals(row.task_id, assignmentTaskId)) ||
          rows.find((row) => row?.original_task_id != null && equals(row.original_task_id, assignmentTaskId));
        if (byTaskId) return byTaskId;
      }

      const assignmentContentId =
        assignmentItem?.contentId ??
        assignmentItem?.content_id ??
        assignmentItem?.video_content_id ??
        assignmentItem?.assignmentId ??
        assignmentItem?.assignment_id;
      if (assignmentContentId != null && assignmentContentId !== "") {
        const byContentId =
          rows.find((row) => row?.content_id != null && equals(row.content_id, assignmentContentId)) ||
          rows.find((row) => row?.video_content_id != null && equals(row.video_content_id, assignmentContentId)) ||
          rows.find((row) => row?.ADAM_ID != null && equals(row.ADAM_ID, assignmentContentId)) ||
          rows.find((row) => row?.CONTENT_ID != null && equals(row.CONTENT_ID, assignmentContentId));
        if (byContentId) return byContentId;
      }

      const assignmentDetail = assignmentItem?.assignmentDetail ?? assignmentItem?.assignment_detail;
      const assignmentGeo = assignmentItem?.geo;

      if (assignmentDetail) {
        const detailMatches = rows.filter(
          (row) => row?.task_name === assignmentDetail || row?.assignment_detail === assignmentDetail
        );
        if (detailMatches.length === 1) return detailMatches[0];
        if (detailMatches.length > 1 && assignmentGeo) {
          const detailGeoMatch = detailMatches.find(
            (row) =>
              row?.geo === assignmentGeo ||
              (Array.isArray(row?.geos) &&
                row.geos.some((geoItem) =>
                  typeof geoItem === "string" ? geoItem === assignmentGeo : geoItem?.geo === assignmentGeo
                ))
          );
          if (detailGeoMatch) return detailGeoMatch;
        }
        if (detailMatches.length) return detailMatches[0];
      }

      if (assignmentGeo) {
        const byGeo = rows.find(
          (row) =>
            row?.geo === assignmentGeo ||
            (Array.isArray(row?.geos) &&
              row.geos.some((geoItem) =>
                typeof geoItem === "string" ? geoItem === assignmentGeo : geoItem?.geo === assignmentGeo
              ))
        );
        if (byGeo) return byGeo;
      }

      return rows[0];
    },
    resolvePayPeriod(assignmentItem, sourceRow) {
      const explicitPayPeriod = this.formValues.payPeriod || sourceRow?.payPeriod || this.tableRows?.[0]?.payPeriod;
      if (explicitPayPeriod) return this.normalizePayPeriod(explicitPayPeriod);

      const deliveryDate = assignmentItem?.individualDeadline
        ? assignmentItem?.deliveryDate
        : this.formValues.deliveryDate;
      return this.normalizePayPeriod(deliveryDate);
    },
    resolveClientRequesterId(assignmentItem, sourceRow) {
      const projectRequesters = this.formValues.relatedProject?.clientRequesters || [];
      if (!projectRequesters.length) return null;

      const selectedRequester = this.normalizeText(this.formValues.clientRequester);
      const sourceRequester = this.normalizeText(
        sourceRow?.client_requester ?? sourceRow?.clientRequester ?? assignmentItem?.client_requester
      );

      const matchedRequester = projectRequesters.find((req) => {
        const firstName = this.normalizeText(req?.user?.firstName);
        const lastName = this.normalizeText(req?.user?.lastName);
        const fullName = `${firstName} ${lastName}`.trim();

        if (this.shakespeare || this.shakespeareLoc) {
          return firstName === selectedRequester || lastName === selectedRequester || fullName === selectedRequester;
        }

        if (sourceRequester) {
          return sourceRequester.includes(firstName) && sourceRequester.includes(lastName);
        }

        if (selectedRequester) {
          return fullName === selectedRequester || selectedRequester.includes(fullName);
        }

        return false;
      });

      return matchedRequester?.user?.id ?? null;
    },
    updateAssignmentsList(individualAssignmentsData) {
      this.individualAssignmentsData = individualAssignmentsData;
      if (this.capacityOpen) {
        this.buildCapacityItems();
      }
    },
    filesUpdated(e) {
      this.filesList = e;
    },
    toggleCapacity() {
      this.capacityOpen = !this.capacityOpen;
      if (this.capacityOpen) {
        this.buildCapacityItems();
      }
    },
    getCurrentAssignments() {
      const grouped = this.$refs.assignmentsSectionComponent?.groupedAssignmentsList
        ?.flatMap((assignmentGroup) => assignmentGroup.items);
      return Array.isArray(grouped) && grouped.length
        ? grouped
        : this.individualAssignmentsData;
    },
    extractCapacityStaff(assignee) {
      if (!assignee || typeof assignee !== "object") return null;
      const staffId =
        assignee.staffId ??
        assignee.id ??
        assignee.user?.id ??
        assignee.staff?.id ??
        null;
      if (staffId == null) return null;

      // Capacity numbers only exist on recommendation-sourced assignees
      // (resource-recommendation rankedStaffs). Plain staff-list picks lack them.
      if (assignee.totalAllowedWords == null && assignee.usedWords == null) return null;

      const name =
        assignee.name ||
        `${assignee.user?.firstName ?? ""} ${assignee.user?.lastName ?? ""}`.trim() ||
        "Unknown";

      return {
        staffId,
        name,
        usedWords: Number(assignee.usedWords) || 0,
        totalAllowedWords: Number(assignee.totalAllowedWords) || 0,
      };
    },
    resolveAssignmentWordCount(assignment) {
      // The word-count input maps to workType1Quantity, so prefer the
      // assignment's explicit wordCount and fall back to that quantity.
      // Keep null when neither is a positive number, so the payload never
      // sends a misleading 0.
      if (!assignment || typeof assignment !== "object") return null;
      const wordCount = Number(assignment.wordCount);
      if (Number.isFinite(wordCount) && wordCount > 0) return wordCount;
      const workTypeQuantity = Number(assignment.workType1Quantity);
      if (Number.isFinite(workTypeQuantity) && workTypeQuantity > 0) return workTypeQuantity;
      return null;
    },
    getAssignmentWordCount(assignment) {
      if (!assignment || typeof assignment !== "object") return 0;
      const wordCount = Number(assignment.wordCount);
      if (Number.isFinite(wordCount) && wordCount > 0) return wordCount;
      const workTypeQuantity = Number(assignment.workType1Quantity);
      if (Number.isFinite(workTypeQuantity) && workTypeQuantity > 0) return workTypeQuantity;
      return 0;
    },
    buildCapacityItems() {
      const assignments = this.getCurrentAssignments() || [];
      // Keyed by staffId + role, so a user who holds several roles gets a
      // separate line per role, each with that role's own capacity.
      const byUserRole = new Map();

      assignments.forEach((assignment) => {
        const wordCount = this.getAssignmentWordCount(assignment);

        ASSIGNEE_KEYS.forEach((assigneeKey) => {
          const staff = this.extractCapacityStaff(assignment?.[assigneeKey]);
          if (!staff) return;
          const role = ROLE_BY_ASSIGNEE_KEY[assigneeKey];
          const key = `${staff.staffId}-${role}`;

          let entry = byUserRole.get(key);
          if (!entry) {
            entry = { ...staff, role, batchWords: 0 };
            byUserRole.set(key, entry);
          } else {
            // Same (user, role) across assignments: allowance is consistent;
            // keep the larger value defensively.
            entry.totalAllowedWords = Math.max(entry.totalAllowedWords, staff.totalAllowedWords);
            entry.usedWords = Math.max(entry.usedWords, staff.usedWords);
          }

          // Each role line accrues the word count of every assignment the user
          // holds that role on (one role per assignment field, so no double-count).
          if (wordCount) {
            entry.batchWords += wordCount;
          }
        });
      });

      this.capacityItems = Array.from(byUserRole.values()).map((staff) => {
        const palette = CAPACITY_ROLE_PALETTE[staff.role] || CAPACITY_ROLE_PALETTE.PRIMARY;
        return {
          staffId: staff.staffId,
          name: staff.name,
          role: staff.role,
          roleLabel: ROLE_LABELS[staff.role] || staff.role,
          usedWords: staff.usedWords,
          totalAllowedWords: staff.totalAllowedWords,
          batchWords: staff.batchWords,
          // Remaining capacity after server-used words plus this page's words.
          // Negative means the assigned words exceed what the user can accept.
          left: staff.totalAllowedWords - (staff.usedWords + staff.batchWords),
          color: palette.color,
          lightColor: palette.lightColor,
        };
      });
    },
    capacityUsedPct(item) {
      if (!item || !item.totalAllowedWords) return 0;
      return Math.min((item.usedWords / item.totalAllowedWords) * 100, 100);
    },
    capacityBatchPct(item) {
      if (!item || !item.totalAllowedWords) return 0;
      const usedPct = Math.min((item.usedWords / item.totalAllowedWords) * 100, 100);
      const batchPct = (item.batchWords / item.totalAllowedWords) * 100;
      // Clamp so the dotted segment never extends past the end of the bar.
      return Math.max(Math.min(batchPct, 100 - usedPct), 0);
    },
    capacityBatchStyle(item) {
      return {
        left: this.capacityUsedPct(item) + "%",
        width: this.capacityBatchPct(item) + "%",
        backgroundImage: `radial-gradient(circle, ${item.color} 1.5px, transparent 1.6px)`,
        backgroundSize: "6px 6px",
      };
    },
    async submit() {
      try {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        await this.flushPendingInputs();
        const latestAssignments = this.$refs.assignmentsSectionComponent?.groupedAssignmentsList
          ?.flatMap((assignmentGroup) => assignmentGroup.items);
        const assignmentsToSubmit = Array.isArray(latestAssignments) && latestAssignments.length
          ? latestAssignments
          : this.individualAssignmentsData;
        if (assignmentsToSubmit !== this.individualAssignmentsData) {
          this.individualAssignmentsData = assignmentsToSubmit;
        }
        if (!assignmentsToSubmit.length) {
          this.showFailAlert("No assignments to create");
          return;
        }
        const sharedAssignmentTitle = this.normalizeAssignmentTitle(this.formValues.assignmentTitle);
        const requestTime = this.$moment(
          new Date().toLocaleString("en-US", {
            timeZone: "America/New_York",
          })
        ).format("hh:mm A");
        const sharedQcDueDate = this.formValues.reviewingDueDate || this.formValues.qcDueDate || null;
        const sharedQcDueTime = this.formValues.reviewingDue || this.formValues.qcDue || null;
        const assignmentsArray = [];
        assignmentsToSubmit.forEach((el) => {
          const sourceRow = this.getSourceRowForAssignment(el);
          const assignmentTitleValue = this.isFieldIndividual("assignmentTitle")
            ? this.normalizeAssignmentTitle(el?.assignmentTitle)
            : sharedAssignmentTitle;
          const selectedDepartmentId = this.resolveDepartmentId(this.formValues.department);
          const sourceDepartmentId = this.resolveDepartmentId(sourceRow?.department || this.tableRows?.[0]?.department);
          const defaultFcDepartmentId = this.shakespeare || this.shakespeareLoc
            ? this.resolveDepartmentId("FC")
            : null;
          const fallbackRequestDate = sourceRow?.request_date || this.tableRows?.[0]?.request_date;
          assignmentsArray.push({
            projectId: this.formValues.relatedProject?.id ?? null,
            department: selectedDepartmentId ?? sourceDepartmentId ?? defaultFcDepartmentId,
            schedule: {
              deliveryDate: el.individualDeadline ? el.deliveryDate : this.formValues.deliveryDate,
              deliveryTime: el.individualDeadline ? el.deliveryDue : (this.formValues.deliveryDue || this.formValues.deliveryTime || null),
              payPeriod: this.resolvePayPeriod(el, sourceRow),
              primaryDueDate: el.individualDeadline ? el.writingDueDate : this.formValues.writingDueDate,
              primaryDueTime: el.individualDeadline ? el.writingDue : this.formValues.writingDue,
              secondaryDueDate: el.individualDeadline ? el.editingDueDate : this.formValues.editingDueDate,
              secondaryDueTime: el.individualDeadline ? el.editingDue : this.formValues.editingDue,
              qcDueDate: el.individualDeadline ? (el.reviewingDueDate || el.qcDueDate || null) : sharedQcDueDate,
              qcDueTime: el.individualDeadline ? (el.reviewingDue || el.qcDue || null) : sharedQcDueTime,
              requestDate: this.formValues.requestDate
                ? this.formValues.requestDate
                : fallbackRequestDate
                ? this.$moment(fallbackRequestDate).format("YYYY-MM-DD")
                : null,
              requestTime: this.formValues.requestTime ? this.formValues.requestTime : requestTime,
            },
            task: {
              taskId: el.taskId,
            },
            assignmentDetails: {
              assignmentDetail: el.assignmentDetail ?? null,
              assignmentId: this.resolveAssignmentId(el, sourceRow),
              assignmentStatus: this.formValues.assignmentStatus,
              assignmentTitle: assignmentTitleValue || null,
              contentId: this.resolveContentId(el, sourceRow),
              emailSubjectLine: this.formValues.emailSubjectLine ?? null,
              fileLocation: "Dropbox",
              genre: this.formValues.genre ?? null,
              geo: el.geo,
              keyword: this.pickFirstValue(
                this.formValues.keyword,
                el.keyword,
                sourceRow?.keyword,
                sourceRow?.new_keyword,
                sourceRow?.newKeyword
              ),
              link: this.formValues.filmLink ?? null,
              mediaType: this.formValues.mediaType ?? null,
              note: this.formValues.note ?? null,
              noteToManager: this.formValues.noteToManager ?? null,
              noteToClient: this.formValues.relatedText ?? null,
              requestType: el.requestType ?? null,
              sourceGeo: "en-GB",
              taskId: el.taskId,
              urgent: !!this.formValues.urgent,
              wordCount: this.resolveAssignmentWordCount(el)
            },
            clientId: this.formValues.relatedProject?.client?.id ?? null,
            clientRequesterId: this.resolveClientRequesterId(el, sourceRow),
            projectManagerId: this.formValues?.relatedProject?.projectManager?.id ?? null,
            primaryId: this.resolveAssigneeId(el?.primaryAssignee, el?.writer),
            secondaryId: this.resolveAssigneeId(el?.secondaryAssignee, el?.editor),
            qcId: this.resolveAssigneeId(el?.qcAssignee, el?.reviewer),
            pricing: {
              purchaseOrderId: this.formValues?.po?.id,
              workTypes: Array.from({ length: 6 }, (_, i) => {
                const index = i + 1;
                const quantityValue = el[`workType${index}Quantity`] || 0;
                return {
                  customerPriceListId: this.getCustomerPriceListId(el[`workType${index}Reference`] || null),
                  quantity: quantityValue != null && quantityValue !== '' ? Number(quantityValue) : null,
                  orderPosition: index
                };
              })
            }
          });
        });
        let valid = true;
        for (const assignment of assignmentsArray) {
          if (!this.normalizeAssignmentTitle(assignment.assignmentDetails?.assignmentTitle)) {
            this.showFailAlert("Assignment title is required");
            valid = false;
            break;
          }
          if (!assignment.projectId) {
            this.showFailAlert("Project is required");
            valid = false;
            break;
          }
          if (!assignment.primaryId) {
            this.showFailAlert("Primary assignee is required");
            valid = false;
            break;
          }
        }
        if (!valid) return;
        try {
          const uploadedFiles = await this.uploadAssignmentFiles();

          const res = await processAssignmentsList(assignmentsArray);

          if (!res || res.status > 204) {
            const failedMsg = res?.data?.statusCode
              ? `${res.data.statusCode}: ${res.data.message}`
              : "Something went wrong!";
            this.showFailAlert(failedMsg);
            return;
          }

          if (uploadedFiles.length) {
            const assignmentIds = (res.data || []).map((el) => el.id).filter(Boolean);
            if (assignmentIds.length) {
              await attachFilesToAssignments({ assignmentIds, files: uploadedFiles });
            }
          }

          if (res && res.status >= 200 && res.status < 300) {
            this.successAlert = true;
          }
        } catch (err) {
          console.error("Upload error:", err);
          this.showFailAlert(err.message || "Something went wrong!");
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    getCustomerPriceListId(itemName) {
      if (!itemName) return null;
      const workType = this.allWorkTypes.find((el) => el.workTypeName === itemName);
      return workType ? workType.id : null;
    },
    getStaffItemId(staffItem) {
      const staff = this.staffList.find(
        (el) => `${el.user.firstName} ${el.user.lastName}` === staffItem
      );
      return staff ? staff.user.id : null;
    },
    getClientRequesterId(clientRequester) {
      const requester = this.formValues.relatedProject?.clientRequesters?.find(
        (el) =>
          `${el.user.firstName} ${el.user.lastName}` ===
          clientRequester
      );
      return requester ? requester.user.id : null;
    },
    async getProjects() {
      await getAllProjects()
        .then((data) => {
          this.setCommonData({ field: "projectsList", data: data });
        })
        .catch((err) => {
          console.error(err);
        });
    },
    async getDepartments() {
      await getSpecificEnum("departments")
        .then((data) => {
          this.setCommonData({ field: "departmentOptions", data: data.data || [] });
        })
        .catch((err) => {
          console.error(err);
        });
    },
    async getPriceLists() {
      await getCustomerPriceList()
        .then((data) => {
          this.allWorkTypes = data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  computed: {
    ...mapGetters("spreadsheetUpload", [
      "files",
      "tableRows",
      "allSourceFilesFound",
      "sourceFile",
      "xContent",
      "shakespeare",
      "shakespeareLoc",
      "cueCopywriting",
      "dubEditor",
      "staffListData",
      "formValues"
    ]),
    ...mapGetters("assignment", {
      departmentOptions: "departmentOptions",
      mediaTypeOptions: "mediaTypeOptions",
      keywordsOptions: "keywordOptions",
    }),
    ...mapGetters("auth", ["user"]),
    fileNames() {
      return this.files.map((f) => f.name);
    },
    sharedModalFields() {
      return (this.assignmentFieldSections?.shared || [])
        .map((fieldKey) => ASSIGNMENT_FIELD_LABELS[fieldKey])
        .filter(Boolean);
    },
    individualModalFields() {
      return (this.assignmentFieldSections?.individual || [])
        .map((fieldKey) => ASSIGNMENT_FIELD_LABELS[fieldKey])
        .filter(Boolean);
    },
    individualEligibleModalFields() {
      return [...INDIVIDUAL_ELIGIBLE_FIELDS]
        .map((fieldKey) => ASSIGNMENT_FIELD_LABELS[fieldKey])
        .filter(Boolean);
    },
    exportedPath() {
      return `${DOMAIN}/${ROUTES.assignmentDashboard}?ppm=${this.$moment(
        this.formValues.payPeriod
      ).format("MMMM")}&ppy=${this.$moment(this.formValues.payPeriod).format(
        "YYYY"
      )}&DeliveryDate=${this.formValues.deliveryDate}&Person=${
        this.user.user.firstName
      }${this.user.user.lastName}&Role=Creator${
        this.formValues.assignmentTitle
          ? `&Search=${this.formValues.assignmentTitle}`
          : ""
      }`;
    },
  },
  async mounted() {
    this.getProjects();
    this.getDepartments();
    this.getPriceLists();
    if (!this.staffListData?.length) {
      let staffList = await getAllStaff();
      this.setstaffListData(staffList);
      this.staffList = _.cloneDeep(this.staffListData);
    } else {
      this.staffList = _.cloneDeep(this.staffListData);
    }
    this.prefillSharedFields(this.tableRows);
  },
  beforeRouteLeave(to, from, next) {
    this.setDubEditor(false);
    next();
  }
};
</script>

<style lang="scss" scoped>
.spreadsheet-upload {
  max-width: 1200px;
  margin: 0 auto;

  &__source-section {
    max-height: 160px;
    transition: max-height 0.15s ease-out;
    &.active {
      max-height: 1000px;
      transition: max-height 0.25s ease-in;
    }
  }
  .submit-container {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    .submit-button {
      padding: 19px;
      background-color: #1976d2;
      opacity: .7;
      border-radius: 4px;
      height: 56px;
      text-transform: uppercase;
      color: #fff;
      font-weight: 700;
      font-size: 14px;
      cursor: pointer;
      font-family: "Roboto", sans-serif;
      transition: all 0.3s ease-in-out;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .submit-button__spinner {
      margin-right: 8px;
    }
    .submit-button:hover {
      opacity: 1;
    }
    .submit-button:disabled {
      opacity: .55;
      cursor: not-allowed;
    }
  }
}

::v-deep {
  .success-alert, .fail-alert {
    position: fixed;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    & .v-alert {
      &__content {
        display: flex;
        align-items: center;
      }
      &__icon {
        align-self: center;
      }
    }
  }
  .v-badge__badge .v-icon {
    margin: -2px -2px 0;
  }
}

.capacity-section {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
  }
  &__chevron {
    transition: transform 0.2s ease;
    &--open {
      transform: rotate(180deg);
    }
  }
  &__body {
    margin-top: 16px;
  }
  &__empty {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
    padding: 8px 0;
  }
}

.capacity-item {
  padding: 16px 0;

  &:first-child {
    padding-top: 0;
  }

  &--divided {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.6);
  }

  &__role {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-left: 6px;
  }

  &__row {
    display: grid;
    grid-template-columns: 110px 1fr 90px;
    align-items: end;
    column-gap: 16px;
  }

  &__col {
    &--current {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    &--left {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
    white-space: nowrap;
    margin-bottom: 4px;

    &--center {
      text-align: center;
    }
    &--right {
      text-align: right;
    }
  }

  &__current {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__current-prefix {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
  }

  &__input {
    width: 64px;
    padding: 4px 8px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;
    font-size: 13px;
    text-align: center;
    color: rgba(0, 0, 0, 0.87);
    background-color: #fff;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: #1976d2;
    }
  }

  &__bar {
    position: relative;
    height: 22px;
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__bar-used {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    transition: width 0.3s ease;
  }

  &__bar-batch {
    position: absolute;
    top: 0;
    height: 100%;
    background-repeat: repeat;
    transition: left 0.3s ease, width 0.3s ease;
  }

  &__bar-text {
    position: relative;
    z-index: 1;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.75);

    &--over {
      color: #d32f2f;
    }
  }
}
</style>
